<template>
    <Panel :title="$t('Filters')" :help-text="$t('DESC_NUMBER_MANAGEMENT_FILTER')">
        <div id="filters_body">
            <div v-if="!more">
                <div id="number_search">
                    <div :class="{ 'number-range': searchMode === 4 }">
                        <template v-if="searchMode === 4">
                            <div>{{ $t('From') }}</div>
                            <CicTextbox
                                id="number_search_from_text"
                                :value="searchFrom"
                                maxlength="64"
                                @input="(val) => (searchFrom = val)"
                            />
                            <div>{{ $t('To') }}</div>
                            <CicTextbox
                                id="number_search_to_text"
                                :value="searchTo"
                                maxlength="64"
                                @input="(val) => (searchTo = val)"
                            />
                        </template>
                        <CicTextbox
                            v-else
                            id="number_search_text"
                            :value="searchText"
                            maxlength="64"
                            @input="(val) => (searchText = val)"
                            @keyup.enter="handleSearch"
                        />
                    </div>
                    <CicSelect
                        id="number_search_field"
                        :value="searchField"
                        :options="filterOptions"
                        @input="(val) => (searchField = val)"
                    />
                    <span class="search" @click="handleSearch"></span>
                </div>

                <CicRadioGroup
                    id="number_search_mode"
                    :value="searchMode"
                    :options="[
                        { value: 0, label: $t('Exact match') },
                        { value: 1, label: $t('Starts with') },
                        { value: 2, label: $t('Ends with') },
                        { value: 3, label: $t('Contains') },
                        { value: 4, label: $t('Range') },
                    ]"
                    @input="(val) => (searchMode = val)"
                />
            </div>
            <div id="quick_filters" :class="{ 'more-filters': !!more }">
                <div
                    v-if="results.types.length > 0 && !more && !searches.some((search) => search.field === 8)"
                    class="quick-filter-section"
                >
                    <div>{{ $t('Access Type') }}</div>
                    <ul>
                        <li v-for="type in results.types" class="quick-filter-option" @click="addSearch(8, type.value)">
                            {{ $t(`ACCESS_TYPE_${type.value}`) }} ({{ type.count }})
                        </li>
                    </ul>
                </div>
                <div
                    v-if="
                        results.prefixes.length > 0 &&
                        (!more || more === 'prefixes') &&
                        !searches.some((search) => search.field === 1)
                    "
                    class="quick-filter-section"
                >
                    <div>{{ $t('Access Prefix') }}</div>
                    <ul :style="{ columns: more ? Math.ceil(results.prefixes.length / 4) : 1 }">
                        <li
                            v-for="prefix in more ? results.prefixes : results.prefixes.slice(0, 4)"
                            class="quick-filter-option"
                            @click="addSearch(1, prefix.value, 1)"
                        >
                            {{ prefix.value }} ({{ prefix.count }})
                        </li>
                        <li
                            v-if="!more && results.prefixes.length > 4"
                            class="quick-filter-option"
                            @click="more = 'prefixes'"
                        >
                            {{ $t('More...') }}
                        </li>
                    </ul>
                </div>
                <div v-if="!more && !searches.some((search) => search.field === 3)" class="quick-filter-section">
                    <div>{{ $t('ROUTE_TYPE') }}</div>
                    <ul>
                        <li
                            v-for="type in results.routeTypes"
                            class="quick-filter-option"
                            @click="addSearch(3, type.value)"
                        >
                            {{ routeTypes[type.value] }} ({{ type.count }})
                        </li>
                    </ul>
                </div>
                <div
                    v-if="
                        $store.hasRight('VIEW_SUBORGANISATIONS') &&
                        (!more || more === 'orgs') &&
                        !searches.some((search) => [2, 5].includes(search.field))
                    "
                    class="quick-filter-section"
                >
                    <div>{{ $t('Organisation') }}</div>
                    <ul :style="{ columns: more ? Math.ceil(results.orgs.length / 4) : 1 }">
                        <li
                            v-for="result in more ? results.orgs : results.orgs.slice(0, 4)"
                            class="quick-filter-option"
                            @click="addSearch(2, result.value)"
                        >
                            {{ result.name }} ({{ result.count }})
                        </li>
                        <li v-if="!more && results.orgs.length > 4" class="quick-filter-option" @click="more = 'orgs'">
                            {{ $t('More...') }}
                        </li>
                    </ul>
                </div>
                <div
                    v-if="(!more || more === 'plans') && !searches.some((search) => [4, 6].includes(search.field))"
                    class="quick-filter-section"
                >
                    <div>{{ $t('Route Plan') }}</div>
                    <ul :style="{ columns: more ? Math.ceil(results.plans.length / 4) : 1 }">
                        <li
                            v-for="plan in more ? results.plans : results.plans.slice(0, 4)"
                            class="quick-filter-option"
                            @click="addSearch(4, plan.value)"
                        >
                            {{ plan.name }} ({{ plan.count }})
                        </li>
                        <li
                            v-if="!more && results.plans.length > 4"
                            class="quick-filter-option"
                            @click="more = 'plans'"
                        >
                            {{ $t('More...') }}
                        </li>
                    </ul>
                </div>
                <div v-if="!more && !searches.some((search) => search.field === 7)" class="quick-filter-section">
                    <div>{{ $t('DR active?') }}</div>
                    <ul>
                        <li v-for="dr in results.drs" class="quick-filter-option" @click="addSearch(7, dr.value)">
                            {{ $t(dr.value ? 'Yes' : 'No') }} ({{ dr.count }})
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div v-if="!more" id="current_filters">
            <ElTag v-for="(filter, index) in filters" closable @close="removeSearch(index)">
                {{ filter.type }} - {{ filter.label }}
            </ElTag>
        </div>
        <template v-if="more" #footer>
            <CicButton id="close_more" @click="more = null">{{ $t('Cancel') }}</CicButton>
        </template>
    </Panel>
</template>

<script>
    import { ElTag } from 'element-plus';
    import CicButton from '@/components/CicButton';
    import Panel from '@/components/container/Panel';
    import CicRadioGroup from '@/components/inputs/CicRadioGroup';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { load } from '@/utils/Http';
    import { getRouteTypeLabels } from '@/utils/RouteCollectionUtils';

    export default {
        name: 'AccessPointFilter',
        components: {
            CicButton,
            CicRadioGroup,
            CicSelect,
            CicTextbox,
            ElTag,
            Panel,
        },
        props: {
            displayGroupNumbers: {
                type: Boolean,
                default: false,
            },
            reload: {
                type: Boolean,
                default: false,
            },
            searches: {
                type: Array,
                required: true,
            },
        },
        emits: ['change'],
        data() {
            return {
                more: null,
                results: { types: [], prefixes: [], orgs: [], plans: [], routeTypes: [], drs: [] },
                searchField: 1,
                searchFrom: '',
                searchMode: 3,
                searchText: '',
                searchTo: '',
            };
        },
        computed: {
            filters() {
                const fields = [
                    '',
                    this.$t('Access Point'),
                    this.$t('Organisation'),
                    this.$t('ROUTE_TYPE'),
                    this.$t('Route Plan'),
                    this.$t('Organisation'),
                    this.$t('Route Plan'),
                    this.$t('DR active?'),
                    this.$t('Access Type'),
                ];

                return this.searches.map((search) => {
                    let label = search.value;
                    switch (search.field) {
                        case 4:
                            label =
                                this.results.plans.length > 0
                                    ? this.results.plans.find((plan) => plan.value === label).name
                                    : '';
                            break;
                        case 7:
                            label = this.$t(label ? 'Yes' : 'No');
                            break;
                        case 8:
                            label = this.$t(`ACCESS_TYPE_${label}`);
                            break;
                        case 3:
                            label = this.routeTypes[label];
                            break;
                        case 2:
                            label =
                                this.results.orgs.length > 0
                                    ? this.results.orgs.find((org) => org.value === label).name
                                    : '';
                            break;
                    }

                    if (search.mode === 4) {
                        return {
                            type: fields[search.field],
                            label: this.$t('[x] To [y]').replace(/\[x]/g, search.from).replace(/\[y]/g, search.to),
                        };
                    }

                    return { type: fields[search.field], label };
                });
            },
            // does not translate properly if done in created()
            filterOptions() {
                const filterOptions = [{ value: 1, label: this.$t('Access Point') }];
                if (this.$store.hasRight('VIEW_SUBORGANISATIONS')) {
                    filterOptions.push({ value: 5, label: this.$t('Organisation') });
                }
                filterOptions.push({ value: 6, label: this.$t('Route Plan') });
                return filterOptions;
            },
            // does not translate properly if done in created()
            routeTypes() {
                return getRouteTypeLabels();
            },
        },
        watch: {
            displayGroupNumbers() {
                this.loadFilters(this.searches);
            },
            reload() {
                this.loadFilters(this.searches);
            },
            searches() {
                this.loadFilters(this.searches);
            },
        },
        created() {
            this.loadFilters(this.searches);
        },
        methods: {
            addSearch(field, value, mode = 0) {
                const searches = JSON.parse(JSON.stringify(this.searches));
                searches.push({ field, value, mode });
                this.$emit('change', searches);
            },
            handleSearch() {
                const searches = [];
                const empty =
                    (this.searchMode !== 4 && this.searchText.trim() === '') ||
                    (this.searchMode === 4 && this.searchFrom.trim() === '' && this.searchTo.trim() === '');
                if (!empty) {
                    if (this.searchMode === 4) {
                        if (
                            this.searchFrom.trim() !== '' &&
                            this.searchTo.trim() !== '' &&
                            this.searchTo >= this.searchFrom
                        ) {
                            searches.push({
                                field: this.searchField,
                                from: this.searchFrom,
                                to: this.searchTo,
                                mode: 4,
                            });
                        } else {
                            return;
                        }
                    } else {
                        searches.push({ field: this.searchField, value: this.searchText, mode: this.searchMode });
                    }
                }

                this.$emit('change', searches);
            },
            loadFilters(searches = []) {
                this.searchField = 1;
                this.searchFrom = '';
                this.searchMode = 3;
                this.searchText = '';
                this.searchTo = '';

                const params = {
                    groupNumbers: this.displayGroupNumbers ? 1 : 0,
                    searches: JSON.stringify(searches),
                };
                load('accesspoints/filters', params, (response) => {
                    this.results = {
                        types: [],
                        prefixes: [],
                        orgs: [],
                        plans: [],
                        routeTypes: [],
                        drs: [],
                        ...response.filters,
                    };
                });
            },
            removeSearch(index) {
                const searches = JSON.parse(JSON.stringify(this.searches));
                searches.splice(index, 1);
                this.$emit('change', searches);
            },
        },
    };
</script>
